"use client";

import { Icon, Text, type TextProps } from "@chakra-ui/react";
import type { ComponentProps } from "react";

interface SidebarNavItemLabelProps extends TextProps {
  isCollapsed?: boolean;
  /** Short labels (e.g. Log out) - don't grow to fill the row width. */
  compact?: boolean;
}

/**
 * Sidebar nav label - wraps long link names instead of clipping them.
 * When collapsed, the label is hidden (tooltip on the parent shows the full text).
 */
export function SidebarNavItemLabel({
  isCollapsed = false,
  compact = false,
  children,
  ...rest
}: SidebarNavItemLabelProps) {
  if (isCollapsed) {
    return (
      <Text
        fontSize="sm"
        fontWeight="400"
        opacity={0}
        w={0}
        overflow="hidden"
        aria-hidden
        whiteSpace="nowrap"
        transition="opacity 0s, width 0s"
        {...rest}
      >
        {children}
      </Text>
    );
  }

  return (
    <Text
      flex={compact ? undefined : "1"}
      minW={compact ? undefined : 0}
      fontSize="sm"
      fontWeight="400"
      lineHeight={compact ? "1.25" : "1.35"}
      whiteSpace={compact ? "nowrap" : "normal"}
      overflowWrap={compact ? undefined : "anywhere"}
      transition="opacity 0.2s ease 0.2s, width 0.2s ease 0.2s"
      {...rest}
    >
      {children}
    </Text>
  );
}

/** Vertically center icon + label in sidebar nav rows. */
export function sidebarNavRowAlign(_isCollapsed?: boolean) {
  return "center" as const;
}

/** Shared sidebar nav icon - block SVG removes baseline offset vs text. */
export function SidebarNavIcon({
  boxSize = 4,
  sx,
  ...props
}: ComponentProps<typeof Icon>) {
  return (
    <Icon
      boxSize={boxSize}
      flexShrink={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        "& svg": {
          display: "block",
        },
        ...sx,
      }}
      {...props}
    />
  );
}
